import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from 'react-redux';
import Question from "../../components/Question";
import MoveButton from "../../components/MoveButton";
import { Stack, Box, useTheme, useMediaQuery } from "@mui/material";
import { callBackendApiGet } from "../../lib/api";
import CustomSpinner from "../../components/CustomSpinner";
import { setMissingPatient, setReturning, setPatients, setCaseId, setSelectedReturningPatient } from '../../redux/userSlice';
import setPageAndDeferNavigate from "../../lib/setPageAndDeferNavigate";
import logDrtEvent from '../../lib/logging';

export default function ReturningPatient() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('sm'));
    const user = useSelector((state) => state.user);
    const [loading, setLoading] = useState(true);
    const location = useLocation();
    const { portal = false } = location.state || {};

    useEffect(() => {
        const hasPatients = Array.isArray(user.patients) && user.patients.length > 0;
        if (hasPatients) {
            setLoading(false);
        } else if (portal || (!user.firstName && !user.lastName && !user.email)) {
            fetchPatientData();
        }
    }, [portal, user.phoneNo, user.patients]);

    React.useEffect(() => {
        if (portal) {
            dispatch(setCaseId(''));
        }
    }, [dispatch]);

    const fetchPatientData = () => {
        setLoading(true);
        const encodedPhoneNo = encodeURIComponent(user.phoneNo);
        callBackendApiGet("/api/v1/ret-patients?q=" + encodedPhoneNo)
            .then((res) => {
                if (!res.ok) {
                    throw new Error(`HTTP error! status: ${res.status}`);
                }
                return res.json();
            })
            .then((data) => {
                setTimeout(() => {
                    if (data && Array.isArray(data.returning_patients)) {
                        dispatch(setPatients(data.returning_patients));
                        if (data.returning_patients.length === 1) {
                            handlePatientSelection(data.returning_patients[0]);
                        } else
                            if (data.returning_patients.length === 0) {
                                if (data.tellescope_patient_exists) {
                                    dispatch(setMissingPatient(true));
                                    logDrtEvent(`[returning] Patient started returning intake but navigated to demographic screen to create a piction patient profile ( tellescope patient profile exists )`);
                                    setPageAndDeferNavigate(dispatch, '/demographic', () =>
                                        navigate('/demographic', { state: { returning: true } })
                                    );
                                } else {
                                    dispatch(setReturning(false));
                                    logDrtEvent(`[returning] Patient started returning intake but navigated to concern screen to create a tellescope and piction patient profile`);
                                    setPageAndDeferNavigate(dispatch, '/concern', () =>
                                        navigate('/concern', { state: { returning: false } })
                                    );
                                }
                            } else {
                                logDrtEvent(`[returning] Patient with more than 1 records`);
                            }
                    } else {
                        navigate("/error", { state: { returning: true } });
                    }
                    setLoading(false);
                }, 2000);
            })
            .catch((error) => {
                console.error("Error fetching patient data:", error);
                setLoading(false);
                navigate("/error", { state: { returning: true } });
            });
    };

    const handlePatientSelection = (patient) => {
        dispatch(setSelectedReturningPatient(patient));

        if (portal) {
            navigate('/returning-reason', { state: { portal } });
        } else {
            setPageAndDeferNavigate(dispatch, '/returning-reason', () =>
                navigate('/returning-reason', { state: { portal } })
            );
        }
    };

    const handleBack = () => {
        if (portal) {
            navigate('/case-list', { state: { portal: portal } });
        } else {
            setPageAndDeferNavigate(dispatch, '/returning', navigate);
        }
    };

    if (loading) {
        return <CustomSpinner />;
    }

    return (
        <Question
            noBack={!portal}
            returning={true}
            onBack={handleBack}
            title="Select a patient"
            subtitle="This follow up visit is for"
            input={
                <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    width="100%"
                >
                    <Stack
                        spacing={2}
                        width="100%"
                        maxWidth="sm"
                        alignItems="center"
                    >
                        {user?.patients.map((patient, index) => (
                            <MoveButton
                                key={index}
                                onClick={() => handlePatientSelection(patient)}
                                title={patient.full_name}
                                selected={patient.full_name === user.returningFullName}
                                sx={{
                                    width: isDesktop ? '100%' : '100%',
                                    maxWidth: '400px',
                                }}
                            />
                        ))}
                    </Stack>
                </Box>
            }
        />
    );
}